import React, { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";

const STATIC_DEFAULT_PROMPT_TEXT =
  'Corrects grammar and punctuation using standard English rules, formats the text neatly, and turns written numbers into symbols (e.g., "five dollars" -> "$5"). No added explanations or edits beyond grammar.';

const PromptModal = ({ isOpen, mode, promptData, onClose, onSubmit, loading }) => {
  const [name, setName] = useState("");
  const [promptText, setPromptText] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (mode === "add") {
        setName("");
        setPromptText("");
      } else if (promptData) {
        setName(promptData.label || promptData.Title || "");
        setPromptText(promptData.promptDispText || promptData.promptText || STATIC_DEFAULT_PROMPT_TEXT);
      }
    }
  }, [isOpen, mode, promptData]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || loading) return;
    onSubmit?.({ name: name.trim(), promptText: promptText.trim() });
  };

  const getTitle = () => {
    switch (mode) {
      case "view":
        return "Preview";
      case "edit":
        return "Edit Prompt";
      case "add":
        return "Add Prompt";
      default:
        return "";
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#0000007a] backdrop-blur-[2px] flex items-center justify-center p-4 font-inter"
      style={{
        animation: "modalBackdropIn 200ms ease-out both",
      }}
    >
      <style>{`
        @keyframes modalBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      <div
        className="bg-white rounded-[24px] border border-[#E5E7EB] w-full max-w-[540px] p-6 sm:p-7 shadow-2xl relative"
        style={{
          animation: "modalIn 300ms cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-[#0F1115] leading-tight">
            {getTitle()}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={`text-[#5C6370] hover:text-[#0F1115] rounded-full p-1.5 transition-colors duration-200 hover:bg-[#F2F3F6] ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="text-[13px] font-semibold text-[#0F1115] mb-2 block">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                readOnly={mode === "view"}
                disabled={mode === "view" || loading}
                placeholder="Enter prompt name"
                className={`w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFC] focus:outline-none text-sm text-[#363636] font-medium transition duration-200 ${
                  mode !== "view" && !loading
                    ? "focus:border-[#3AB1F6] focus:bg-white focus:shadow-sm"
                    : "cursor-default opacity-85 select-none"
                }`}
              />
            </div>

            <div>
              <label className="text-[13px] font-semibold text-[#0F1115] mb-2 block">
                Prompt
              </label>
              <textarea
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                readOnly={mode === "view"}
                disabled={mode === "view" || loading}
                placeholder="Enter prompt text"
                rows={5}
                className={`w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFC] focus:outline-none text-sm text-[#5C6370] leading-relaxed resize-none transition duration-200 custom-scrollbar ${
                  mode !== "view" && !loading
                    ? "focus:border-[#3AB1F6] focus:bg-white focus:shadow-sm"
                    : "cursor-default opacity-85 select-none"
                }`}
              />
            </div>
          </div>

          {/* Footer Buttons */}
          {mode !== "view" && (
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className={`flex-1 py-3 bg-[#E5E7EB] text-[#5C6370] hover:bg-[#D8DBE0] rounded-xl font-semibold transition duration-200 text-sm text-center ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-3 bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:opacity-95 text-white rounded-xl font-semibold transition duration-200 text-sm text-center shadow-md hover:shadow-lg ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Saving..." : mode === "edit" ? "Save Prompt" : "Add Prompt"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PromptModal;
